<template>
  <div v-if="!isLoading && hasData" class="quality">
    <span class="input-label">{{ $t(`quality.inputLabel`) }}</span>
    <base-number-input :label="$t(`quality.input`)"
                       :value="indicators.preparedPallets"
                       @update="onPreparedPalletsChange" />
    <table>
      <thead>
      <tr>
        <th><span>N°</span></th>
        <th><span>{{ $t(`quality.tableTitle.label`) }}</span></th>
        <th v-html="$t(`quality.tableTitle.errors`)" />
      </tr>
      </thead>
      <tbody class="table-body">
      <tr v-for="(criteria, i) in indicators.criteria" :key="i">
        <td><span class="bold">{{ `C.${criteria.sid.toString().padStart(2, '0')}` }}</span></td>
        <td class="left"><span>{{ criteria.label }}</span></td>
        <td>
          <input :disabled="!hasPreparedPallets"
                 :value="criteria.error"
                 type="number"
                 @input="onCriteriaChange({ key: i, value: $event.target.valueAsNumber })">
        </td>
      </tr>
      </tbody>
    </table>

    <div class="calculatedRate">
      <div class="label">{{ $t(`quality.tableRows.calculatedRate`) }}</div>
      <div class="rate">{{ `${calculatedRate}%` }}</div>
    </div>
    <floating-button class="floating-validate"
                     color="green"
                     icon="accept"
                     text="Valider"
                     @validate="sendDatas()" />
  </div>
  <loader v-else-if="isLoading" color="#DA4453" />
  <no-data v-else />
</template>

<script>
import indicatorsService from '@/services/indicatorsService'
import { debounced } from '@/utils/decorators'
import { sendErrorToast } from '@/utils/utils'
import { mapGetters, mapMutations } from 'vuex'
import BaseNumberInput from "@/components/base/BaseNumberInput.vue";
import FloatingButton from "@/components/base/floatingButton.vue";
import Loader from "@/components/base/Loader.vue";
import NoData from "@/components/base/NoData.vue";

export default {
  name: 'MeshQuality',
  components: {NoData, Loader, FloatingButton, BaseNumberInput},
  data() {
    return {
      indicators: null,
      isLoading: false
    }
  },

  watch: {
    'site'() {
      if (this.isValidContext) this.fetchData()
    },

    'meshSite'() {
      if (this.isValidContext) this.fetchData()
    },

    'period'() {
      if (this.isValidContext) this.fetchData()
    }
  },

  created() {
    this.fetchData()
  },

  computed: {
    ...mapGetters('context', ['site','meshSite', 'period', 'isValidContext']),

    hasData() {
      return this.indicators && this.indicators.criteria.length > 0
    },

    hasPreparedPallets() {
      return this.indicators && this.indicators.preparedPallets > 0
    },

    calculatedRate() {
      const errors = this.hasData ? this.indicators.criteria.reduce((acc, criteria) => acc + (criteria.error ? criteria.error : 0), 0) : 0
      const divider = (this.hasData ? this.indicators.criteria.length : 0) * this.indicators.preparedPallets
      return errors > 0 ? ((errors / divider) * 100).toFixed(2) : 0
    }
  },

  methods: {
    ...mapMutations('context', ['SET_MESH_SITE']),

    onPreparedPalletsChange({ value }) {
      this.indicators.preparedPallets = value
      if (value < 0) {
        this.indicators.preparedPallets = null
        sendErrorToast(this.$t('quality.errors.valIsInfZero'))
      }
    },

    onCriteriaChange({ key, value }) {
      const initialValue = this.indicators.criteria[key].error
      const criteriaSupControlledPallets = value > this.indicators.preparedPallets
      //Force Dom Change by assigning the newValue even if there's an error to reload input value and then check error.
      this.indicators.criteria[key].error = value
      if (criteriaSupControlledPallets || value < 0) {
        this.indicators.criteria[key].error = initialValue
        sendErrorToast(this.$t(criteriaSupControlledPallets ? 'quality.errors.criteriaSupControlledPallets' : 'quality.errors.valInfZero'))
      }
    },

    buildIndicators() {
      return {
        customer: this.meshSite,
        site: this.site,
        preparedPallets: this.indicators.preparedPallets,
        userCode: this.$userInfos && this.$userInfos.employeeNumber ? this.$userInfos.employeeNumber : '',
        criteria: this.indicators.criteria
      }
    },

    async fetchData() {
      this.isLoading = true
      this.indicators = await indicatorsService.getQualityIndicators({
        site: this.site,
        customer: this.meshSite,
        date: this.period
      })
      this.isLoading = false
    },

    @debounced(1000)
    async sendDatas() {
      this.isLoading = true
      const indicators = this.buildIndicators()
      await indicatorsService.setQualityIndicators({
        site: this.site,
        customer: this.meshSite,
        date: this.period,
        indicators
      })
      this.SET_MESH_SITE(null)
      this.isLoading = false
    }
  }
}
</script>

<style lang="scss" scoped>
.quality, .v-loader, .noData {
  padding: 2rem 4rem 2rem 4rem;
  height: calc(82% - 40px);
  overflow-y: auto;
  background-color: $app-background;

  .input-label {
    font: 15px $primary-font;
    color: #2C5364;
    font-weight: bold;
  }

  .base-number-input {
    margin-top: 1rem;
    width: 550px;
  }

  table {
    width: 550px;
    margin-top: 2rem;
    border-collapse: collapse;
    font-family: $secondary-font;
    border: 1px solid #E7E8EA;
    box-shadow: 5px 5px 10px rgba(0, 0, 0, 0.15);

    th {
      background: $light-red;
      font-weight: normal;
      color: white;
      position: relative;
      font-size: 14px;
      height: 40px;
      padding: 0 15px;
    }

    tbody {
      background-color: white;

      .left {
        @include display-flex(center, flex-start, row);
      }

      .bold {
        font-weight: 600;
      }

      td {
        min-height: 39px;
        padding: 5px 15px;
        text-align: center;
        color: #707070;
        font-size: 13px;
      }

      input {
        width: 45px;
        height: 24px;
        border: 2px solid #D8D7D9;
        color: #707070;
        text-align: center;
        font-size: 13px;
      }

      input[type=number]::-webkit-inner-spin-button,
      input[type=number]::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
    }
  }

  .calculatedRate {
    margin-top: 2rem;
    width: 550px;
    height: 40px;
    padding: 5px 0;
    background-color: white;
    box-shadow: 5px 5px 10px rgba(0, 0, 0, 0.15);
    @include display-flex(center, space-between, row);

    .label, .rate {
      min-height: 40px;
      color: #707070;
      font-weight: 600;
      font-family: $secondary-font;
      font-size: 13px;
      display: flex;
      align-items: center;
    }

    .label {
      margin-left: 85px;
    }

    .rate {
      margin-right: 75px;
    }
  }
}
</style>
